/**
 * Analytics Service — V2.M.1
 *
 * Client-side helper that emits events to /api/analytics/track for
 * both anonymous and authenticated users.
 *
 * ⚠️ Privacy posture (operator decision, V2.M.1) :
 *   - No consent prompt — tracking starts on first load.
 *   - Session ID is a UUID v4 persisted in localStorage.
 *   - All transport is fire-and-forget (sendBeacon when available)
 *     and silently swallows errors so analytics NEVER interferes
 *     with user-facing behaviour.
 *   - Server (api/analytics/track) stores raw IP, raw user-agent,
 *     and raw wallet addresses. See ROADMAP for tradeoff context.
 */

import { supabase } from '../lib/supabase/client';

const SESSION_ID_KEY = 'analytics_session_id';
const ENDPOINT = '/api/analytics/track';

// ─────────────────────────────────────────────────────────────────────
// Session ID (UUID v4 in localStorage)
// ─────────────────────────────────────────────────────────────────────

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 fallback (older browsers / jsdom).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let id = window.localStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = generateUuid();
      window.localStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
}

// ─────────────────────────────────────────────────────────────────────
// Event emission
// ─────────────────────────────────────────────────────────────────────

export interface TrackOptions {
  page_path?: string;
  /** Force flush via fetch (await), even if sendBeacon is available. */
  await?: boolean;
}

export async function trackEvent(
  eventType: string,
  payload: Record<string, unknown> = {},
  options: TrackOptions = {},
): Promise<void> {
  if (typeof window === 'undefined') return;

  const sessionId = getOrCreateSessionId();
  if (!sessionId) return;

  // Read the current user id (anonymous if no session).
  let userId: string | null = null;
  try {
    const { data } = await supabase.auth.getSession();
    userId = data?.session?.user?.id || null;
  } catch {
    /* anonymous fallback */
  }

  const body = JSON.stringify({
    session_id: sessionId,
    event_type: eventType,
    payload,
    page_path: options.page_path || (typeof location !== 'undefined' ? location.pathname : null),
    user_id: userId,
  });

  // Prefer sendBeacon for fire-and-forget (survives navigations).
  if (!options.await && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      const sent = navigator.sendBeacon(ENDPOINT, blob);
      if (sent) return;
    } catch {
      // Fall through to fetch.
    }
  }

  // Fallback to fetch, silent on failure.
  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    /* Silent — analytics never interferes with UX */
  }
}

// ─────────────────────────────────────────────────────────────────────
// Convenience helpers — one per common event type
// ─────────────────────────────────────────────────────────────────────

export function trackPageView(path: string, title?: string): Promise<void> {
  return trackEvent('page_view', { path, title }, { page_path: path });
}

export function trackTokenAdded(token: { id: string; symbol?: string; chain?: string }): Promise<void> {
  return trackEvent('add_token', {
    token_id: token.id,
    symbol: token.symbol,
    chain: token.chain,
  });
}

export function trackTokenRemoved(token: { id: string; symbol?: string }): Promise<void> {
  return trackEvent('remove_token', {
    token_id: token.id,
    symbol: token.symbol,
  });
}

export function trackWalletLinked(chain: string, address: string): Promise<void> {
  // Address is sent raw to the endpoint and persisted as-is on the
  // session row (operator decision — V2.M.1).
  return trackEvent('link_wallet', { chain, address });
}

export function trackWalletUnlinked(chain: string, address: string): Promise<void> {
  return trackEvent('unlink_wallet', { chain, address });
}

export function trackAuth(action: 'sign_in' | 'sign_up' | 'sign_out'): Promise<void> {
  return trackEvent(action, {});
}
